import { useEffect, useRef, useState, type KeyboardEvent } from "react"

import { SOFIA_BIG_DOG_PROMPT, SOFIA_DISPLAY_NAME } from "@/constants/sofia"
import { useUser } from "@clerk/nextjs"
import { Button, cn, Textarea } from "@coval/ui"
import { ArrowUp } from "lucide-react"

import { openCoviWithPrompt } from "@/lib/coviEvents"
import { useRareSuggestion } from "@/hooks/useRareSuggestion"

import { HOME_CONTENT_COLUMN } from "../utils/homeContentColumn"
import {
  getPacificGreeting,
  HOME_SOFIA_ONBOARDING_SUGGESTED_PROMPTS,
  HOME_SOFIA_SUGGESTED_PROMPTS,
} from "../utils/homeSofiaPrompts"

const HOME_SOFIA_GREETING_REFRESH_MS = 60000

// Keeps the pill row to a couple of lines so the composer stays the focus of
// the hero rather than the suggestions underneath it.
const HOME_SOFIA_VISIBLE_SUGGESTIONS = 4

interface HomeSofiaHeroProps {
  variant?: "active" | "onboarding"
  suggestions?: readonly string[]
}

export function HomeSofiaHero({
  variant = "active",
  suggestions: suggestedPrompts,
}: HomeSofiaHeroProps) {
  const { user } = useUser()
  const [message, setMessage] = useState("")
  const [greeting, setGreeting] = useState(getPacificGreeting)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const firstName = user?.firstName?.trim()

  // The composer is the landing's primary action, so it takes focus as soon as
  // the hero mounts and the user can type without clicking first.
  useEffect(() => {
    composerRef.current?.focus()
  }, [])

  useEffect(() => {
    const syncGreeting = () => {
      setGreeting(getPacificGreeting())
    }
    const interval = setInterval(syncGreeting, HOME_SOFIA_GREETING_REFRESH_MS)
    document.addEventListener("visibilitychange", syncGreeting)
    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", syncGreeting)
    }
  }, [])

  const suggestions = useRareSuggestion(
    (
      suggestedPrompts ??
      (variant === "onboarding"
        ? HOME_SOFIA_ONBOARDING_SUGGESTED_PROMPTS
        : HOME_SOFIA_SUGGESTED_PROMPTS)
    ).slice(0, HOME_SOFIA_VISIBLE_SUGGESTIONS),
    SOFIA_BIG_DOG_PROMPT,
  )

  const title =
    variant === "onboarding"
      ? `Welcome to Coval${firstName ? `, ${firstName}` : ""}`
      : `${greeting}${firstName ? `, ${firstName}` : ""}`

  const handleSubmit = () => {
    const prompt = message.trim()
    if (!prompt) {
      return
    }
    openCoviWithPrompt(prompt, { surface: "home" })
    setMessage("")
  }

  const handleSelectSuggestion = (prompt: string) => {
    openCoviWithPrompt(prompt, { surface: "home" })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  return (
    <section
      aria-labelledby="home-sofia-title"
      className={cn(HOME_CONTENT_COLUMN, "flex flex-col gap-6")}
    >
      <div className="flex items-center justify-center gap-3">
        <h1
          id="home-sofia-title"
          className="text-2xl font-semibold tracking-tight text-coval-text"
        >
          {title}
        </h1>
      </div>

      <div className="flex flex-col gap-3">
        <Textarea
          ref={composerRef}
          aria-label={`Ask ${SOFIA_DISPLAY_NAME}`}
          value={message}
          onChange={(event) => {
            setMessage(event.target.value)
          }}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={`Ask ${SOFIA_DISPLAY_NAME} anything`}
          className="min-h-16 max-h-64 text-base"
          containerClassName="p-4 rounded-md"
          endAdornment={
            <Button
              icon
              onClick={handleSubmit}
              disabled={!message.trim()}
              tooltip={`Ask ${SOFIA_DISPLAY_NAME}`}
            >
              <ArrowUp aria-hidden />
            </Button>
          }
        />

        <div
          role="group"
          aria-label="Suggested prompts"
          className="flex flex-wrap justify-center gap-2"
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => {
                handleSelectSuggestion(suggestion)
              }}
              className="max-w-full rounded-full border border-coval-border bg-surface-default px-3 py-1.5 text-left text-sm leading-snug text-coval-text-muted transition-colors hover:bg-surface-1 hover:text-coval-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
